import React, { useState, useMemo } from "react";
import {
  UsersIcon,
  XIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/outline";
import FloatingUserHistory from "./FloatingUserHistory";

interface OnlineUser {
  id: string;
  name: string;
  user_type: "banker" | "bidder" | "admin";
}

interface BidderSessionData {
  event_uuid: string;
  event_name: string;
  total_users: number;
  total_sessions: number;
  users: Array<{
    user_uuid: string;
    event_uuid: string;
    bidder_name: string;
    bidder_email: string;
    total_session_duration_ms: number;
    total_connections: number;
    first_connection: string;
    last_disconnection: string;
    sessions: Array<{
      session_id: string;
      connected_at: string;
      disconnected_at: string | null;
      session_duration_ms: number;
      is_active: boolean;
    }>;
  }>;
}

interface UserTypeConfig {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  iconColor: string;
  badgeColor: string;
  bgColor: string;
}

interface FloatingOnlineUsersProps {
  onlineMembers: OnlineUser[];
  position?: "left" | "right";
  initiallyOpen?: boolean;
  status: string;
  bidderSessionData: BidderSessionData;
}

const FloatingOnlineUsers: React.FC<FloatingOnlineUsersProps> = ({
  onlineMembers,
  position = "right",
  initiallyOpen = false,
  status,
  bidderSessionData,
}) => {
  const [isOpen, setIsOpen] = useState(initiallyOpen);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    { bidder: true } // Always keep bidders expanded
  );

  const allowedUserTypes = useMemo(() => ["bidder"], []);

  // Filter and group users by user_type
  const groupedUsers = useMemo(() => {
    const filteredUsers = onlineMembers.filter((user) =>
      allowedUserTypes.includes(user.user_type)
    );

    return filteredUsers.reduce((acc, user) => {
      if (!acc[user.user_type]) {
        acc[user.user_type] = [];
      }
      acc[user.user_type].push(user);
      return acc;
    }, {} as Record<string, OnlineUser[]>);
  }, [onlineMembers, allowedUserTypes]);

  const totalOnline = Object.values(groupedUsers).reduce(
    (total, users) => total + users.length,
    0
  );

  // User type configuration
  const userTypeConfig: Record<string, UserTypeConfig> = {
    bidder: {
      label: "Bidders",
      icon: UsersIcon,
      color: "bg-green-50 border-green-200 text-green-700",
      iconColor: "text-brand-orange",
      badgeColor: "bg-brand-lightOrange text-brand-orange",
      bgColor: "bg-brand-orange",
    },
    banker: {
      label: "Bankers",
      icon: UsersIcon,
      color: "bg-blue-50 border-blue-200 text-blue-700",
      iconColor: "text-brand-orange",
      badgeColor: "bg-brand-lightOrange text-brand-orange",
      bgColor: "bg-brand-orange",
    },
    admin: {
      label: "Administrators",
      icon: UsersIcon,
      color: "bg-purple-50 border-purple-200 text-purple-700",
      iconColor: "text-brand-orange",
      badgeColor: "bg-brand-lightOrange text-brand-orange",
      bgColor: "bg-brand-orange",
    },
  };

  // Get config for user type with fallback
  const getConfigForUserType = (userType: string): UserTypeConfig => {
    return (
      userTypeConfig[userType] || {
        label: userType.charAt(0).toUpperCase() + userType.slice(1),
        icon: UsersIcon,
        color: "bg-gray-50 border-gray-200 text-gray-700",
        iconColor: "text-brand-orange",
        badgeColor: "bg-brand-lightOrange text-brand-orange",
        bgColor: "bg-brand-orange",
      }
    );
  };

  // Generate user avatar initials
  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Toggle group expansion
  const toggleGroup = (userType: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [userType]: !prev[userType],
    }));
  };

  // If status is "end", render the FloatingUserHistory component
  if (status === "end") {
    return (
      <FloatingUserHistory
        bidderSessionData={bidderSessionData}
        position={position}
        initiallyOpen={initiallyOpen}
      />
    );
  }

  if (totalOnline === 0) {
    return (
      <div className={`fixed ${position}-4 bottom-4 z-50`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
        >
          <UsersIcon className="h-6 w-6 text-gray-600" />
        </button>

        {isOpen && (
          <div
            className={`absolute ${
              position === "right" ? "right-0" : "left-0"
            } bottom-16 mb-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden`}
          >
            <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-lg font-semibold text-gray-900">Online</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
              >
                <XIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
            <div className="p-6 text-center text-gray-500">
              <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm">No bidders online</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`fixed ${position}-4 bottom-4 z-50`}>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`relative bg-white rounded-full p-3 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200 ${
          isOpen ? "bg-blue-50 border-blue-300" : ""
        }`}
      >
        <UsersIcon className="h-6 w-6 text-gray-600" />
        {status !== "end" && totalOnline > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
            {totalOnline > 99 ? "99+" : totalOnline}
          </span>
        )}
      </button>

      {/* Floating Panel */}
      {isOpen && (
        <div
          className={`absolute ${
            position === "right" ? "right-0" : "left-0"
          } bottom-16 mb-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden`}
        >
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-2">
              <UsersIcon className="h-5 w-5 text-gray-600" />
              <h3 className="text-lg font-semibold text-gray-900">Online</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-gray-200 transition-colors"
              >
                <XIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          </div>

          {/* Compact View with Accordion */}
          <div className="max-h-96 overflow-y-auto">
            {Object.entries(groupedUsers).map(([userType, users]) => {
              const config = getConfigForUserType(userType);
              const IconComponent = config.icon;
              const isExpanded = expandedGroups[userType];

              return (
                <div
                  key={userType}
                  className="border-b border-gray-100 last:border-b-0"
                >
                  {/* Group Header - Clickable for accordion */}
                  <div
                    className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => toggleGroup(userType)}
                  >
                    <div className="flex items-center space-x-2">
                      <IconComponent
                        className={`h-4 w-4 ${config.iconColor}`}
                      />
                      <span className="text-sm font-medium">
                        {config.label}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${config.badgeColor}`}
                      >
                        {users.length}
                      </span>
                      {isExpanded ? (
                        <ChevronUpIcon className="h-4 w-4 text-gray-500" />
                      ) : (
                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                      )}
                    </div>
                  </div>

                  {/* Expanded User List */}
                  {isExpanded && (
                    <div className="px-3 py-3 bg-gray-50">
                      <div className="space-y-1">
                        {users.map((user) => (
                          <div
                            key={user.id}
                            className="flex items-center justify-between p-2 bg-white rounded border border-gray-200 min-w-0"
                          >
                            <div className="flex items-center space-x-2 min-w-0 flex-1 overflow-hidden">
                              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
                              <div
                                className={`w-6 h-6 rounded-full ${config.bgColor} flex items-center justify-center text-white text-xs font-medium flex-shrink-0`}
                              >
                                {getUserInitials(user.name)}
                              </div>
                              <span className="text-sm font-medium truncate min-w-0 flex-1">
                                {user.name}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default FloatingOnlineUsers;
